import { BaseService } from '@common/http-layer/service/base.service'
import type {
  ChannelSectionPayload,
  CreateChannelSectionInput,
  IChannelsWithSectionsResponsePayload,
  MoveChannelSectionPlacementInput,
  UpdateChannelSectionInput,
} from '../types'

export class ChannelSectionsService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '',
      serviceName: 'ChannelSectionsService',
    })
  }

  private getChannelsBasePath(locationId: string, groupId: string): string {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')

    return `/${locationId}/groups/${groupId}/channels`
  }

  private getSectionsBasePath(locationId: string, groupId: string): string {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')

    return `/${locationId}/groups/${groupId}/channel-sections`
  }

  async fetchChannelsWithSections(groupId: string, locationId: string, noCache?: boolean): Promise<IChannelsWithSectionsResponsePayload> {
    const groupPath = this.getChannelsBasePath(locationId, groupId)
    const headers: Record<string, string> = noCache ? { 'Cache-Control': 'no-cache' } : {}
    const { data } = await this.get<IChannelsWithSectionsResponsePayload>(groupPath, {
      headers,
      params: {
        includeSectionData: true,
      },
      groupId,
      locationId,
    })

    return data
  }

  async createSection(data: CreateChannelSectionInput & { groupId: string; locationId: string }): Promise<ChannelSectionPayload> {
    const sectionsPath = this.getSectionsBasePath(data.locationId, data.groupId)
    const { data: responseData } = await this.post<ChannelSectionPayload>(sectionsPath, data, {
      groupId: data.groupId,
      locationId: data.locationId,
    })

    return responseData
  }

  async updateSection(data: UpdateChannelSectionInput & { groupId: string; locationId: string }): Promise<ChannelSectionPayload> {
    const sectionsPath = this.getSectionsBasePath(data.locationId, data.groupId)
    const { sectionId, groupId, locationId, ...payload } = data
    const { data: responseData } = await this.patch<ChannelSectionPayload>(`${sectionsPath}/${sectionId}`, payload, {
      groupId,
      locationId,
    })

    return responseData
  }

  async moveSection(groupId: string, locationId: string, placement: MoveChannelSectionPlacementInput): Promise<{ success: boolean }> {
    const sectionsPath = this.getSectionsBasePath(locationId, groupId)
    const { data } = await this.patch<{ success: boolean }>(
      `${sectionsPath}/${placement.sectionId}/move`,
      {
        targetParentSectionId: placement.targetParentSectionId,
        prevSiblingId: placement.prevSiblingId ?? null,
        nextSiblingId: placement.nextSiblingId ?? null,
      },
      {
        groupId,
        locationId,
      }
    )

    return data
  }

  async deleteSection(sectionId: string, groupId: string, locationId: string): Promise<void> {
    const sectionsPath = this.getSectionsBasePath(locationId, groupId)

    await this.delete(`${sectionsPath}/${sectionId}`, {
      groupId,
      locationId,
    })
  }
}
